import React from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import '../DailySandAllocations/DailySandAllocations.scss';

export interface JobMetricHistoryEntry {
    id: number | null;
    fromDate: string | null;
    toDate: string | null;
    value: number | null;
}

interface JobMetricMatrixTableProps {
    rowLabel: string;
    valueLabel: string;
    dateList: string[];
    dayDates: Date[];
    entries: JobMetricHistoryEntry[];
    loading: boolean;
    isEditMode?: boolean;
    editedValues?: Record<number, number | null>;
    onCellChange?: (dayIndex: number, value: number | null) => void;
}

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Most-recent-entry-wins: among ranges covering the day, the highest id
// (i.e., the row most recently inserted) supersedes prior overlapping rows.
const getEffectiveValue = (day: Date, entries: JobMetricHistoryEntry[]): number | null => {
    const dayMs = startOfDay(day).getTime();
    let bestValue: number | null = null;
    let bestId = -Infinity;
    for (const entry of entries) {
        if (entry.fromDate == null || entry.toDate == null || entry.value == null) continue;
        const fromMs = startOfDay(new Date(entry.fromDate)).getTime();
        const toMs = startOfDay(new Date(entry.toDate)).getTime();
        if (dayMs < fromMs || dayMs > toMs) continue;
        const id = entry.id ?? -Infinity;
        if (id > bestId) {
            bestId = id;
            bestValue = entry.value;
        }
    }
    return bestValue;
};

const JobMetricMatrixTable = ({
    rowLabel,
    valueLabel,
    dateList,
    dayDates,
    entries,
    loading,
    isEditMode = false,
    editedValues = {},
    onCellChange,
}: JobMetricMatrixTableProps): React.ReactElement => {
    const getDisplayValue = (dayIndex: number, effective: number | null): number | null => {
        if (Object.prototype.hasOwnProperty.call(editedValues, dayIndex)) {
            const v = editedValues[dayIndex];
            return v !== null && Number.isFinite(v) ? v : null;
        }
        return effective;
    };

    const cellValues: (number | null)[] = dayDates.map((d, i) =>
        getDisplayValue(i, getEffectiveValue(d, entries)),
    );

    return (
        <Col>
            <Row>
                <Table
                    bordered
                    className={
                        loading
                            ? 'DailySandAllocationsTable DailySandAllocationsTable-Loading'
                            : 'DailySandAllocationsTable'
                    }
                >
                    <tr className="DailySandAllocationsTable-Dates">
                        <th></th>
                        <th></th>
                        <th></th>
                        {dateList.map((date, index) => (
                            <th key={index} className={date.includes('|') ? 'DailySandAllocations-Weekend' : ''}>
                                {date.replace('|', '')}
                            </th>
                        ))}
                    </tr>

                    <tr>
                        <th className="DailySandAllocations-Header DailySandAllocations-ClmLg" colSpan={2}>
                            {rowLabel}
                        </th>
                        <th className="DailySandAllocations-Header DailySandAllocations-ClmLg">{valueLabel}</th>
                        {cellValues.map((value, i) => (
                            <td key={i}>
                                {isEditMode ? (
                                    <input
                                        inputMode="numeric"
                                        type="text"
                                        className="DailySandAllocations-Edit-Input"
                                        value={value === null ? '' : String(value)}
                                        maxLength={5}
                                        onChange={(e) => {
                                            if (!onCellChange) return;
                                            const raw = e.target.value;
                                            if (raw === '') {
                                                onCellChange(i, null);
                                                return;
                                            }
                                            if (!/^\d{0,5}$/.test(raw)) return;
                                            const parsed = parseInt(raw, 10);
                                            onCellChange(i, Number.isFinite(parsed) ? parsed : null);
                                        }}
                                    />
                                ) : (
                                    value ?? '-'
                                )}
                            </td>
                        ))}
                    </tr>
                </Table>
            </Row>
        </Col>
    );
};

export default JobMetricMatrixTable;
